import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import clsx from 'clsx';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import UiCard from '../components/ui/UiCard';
import { FORMA_LINK_BTN } from '../utils/listPageUi';
import { getDashboardChartTokens } from '../utils/chartTheme';
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  PlusIcon,
  EyeIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { dashboardAPI, coursesAPI, classroomsAPI, sessionsAPI, benefitsAPI } from '../services/api';

const linkSubtle =
  'text-sm font-semibold text-forma-700 hover:text-forma-600 dark:text-forma-400 dark:hover:text-forma-300';

const Dashboard = () => {
  const { isDark } = useTheme();
  const chart = React.useMemo(() => getDashboardChartTokens(isDark), [isDark]);

  const [courseDistribution, setCourseDistribution] = useState([]);
  const [occupancyData, setOccupancyData] = useState([]);

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery(
    'dashboardOverview',
    () => dashboardAPI.getOverview(),
    {
      onError: (error) => console.error('Error fetching dashboard data:', error),
      staleTime: 5 * 60 * 1000,
    }
  );

  const { isLoading: coursesLoading } = useQuery('courses', () => coursesAPI.getAll(), {
    onSuccess: (response) => {
      const courses = response.data?.courses || [];
      if (courses.length === 0) return;

      const categoryCounts = {};
      courses.forEach((course) => {
        const category = course.category || 'other';
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      });

      const colors = {
        'web-development': '#dc2626',
        'graphic-design': '#7c3aed',
        'system-administration': '#64748b',
        'mobile-development': '#ea580c',
        database: '#db2777',
        cybersecurity: '#991b1b',
        default: '#64748b',
        other: '#94a3b8',
      };

      const distribution = Object.entries(categoryCounts).map(([category, count]) => ({
        name: category
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        value: count,
        color: colors[category] || colors.default,
      }));

      setCourseDistribution(distribution);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery(
    'upcomingSessions',
    () => sessionsAPI.getAll({ limit: 5, status: 'scheduled' }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { isLoading: classroomsLoading } = useQuery('classrooms', () => classroomsAPI.getAll(), {
    onSuccess: () => {
      const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

      const occupancy = days.map((day, index) => {
        const baseOccupancy = index >= 5 ? 30 + Math.random() * 20 : 70 + Math.random() * 20;
        return { name: day, occupancy: Math.round(baseOccupancy) };
      });

      setOccupancyData(occupancy);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: benefitsData, isLoading: benefitsLoading } = useQuery(
    'benefitsTrends',
    () => benefitsAPI.getTrends({ year: new Date().getFullYear() }),
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  const revenueChartData = React.useMemo(() => {
    if (!benefitsData?.data?.months) {
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul'];
      return months.map((month) => ({ name: month, montant: 0 }));
    }

    return benefitsData.data.months.map((month) => ({
      name: month.monthName.substring(0, 3),
      montant: month.income || 0,
    }));
  }, [benefitsData]);

  const mainStats = React.useMemo(() => {
    const data = dashboardData?.data || {};

    return [
      {
        title: 'Étudiants Actifs',
        value: data.members?.active || '0',
        change: `${data.members?.growthRate > 0 ? '+' : ''}${data.members?.growthRate || 0}%`,
        positive: data.members?.growthRate >= 0,
        icon: AcademicCapIcon,
        iconWrap:
          'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
        link: '/students',
      },
      {
        title: 'Cours en cours',
        value: data.courses?.active || '0',
        change: `+${data.courses?.newThisMonth || 0}`,
        positive: true,
        icon: ClipboardDocumentCheckIcon,
        iconWrap:
          'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
        link: '/courses',
      },
      {
        title: 'Revenus du mois',
        value: `${data.revenue?.thisMonth?.toLocaleString() || '0'} DA`,
        change: `${data.revenue?.growthRate > 0 ? '+' : ''}${data.revenue?.growthRate || 0}%`,
        positive: data.revenue?.growthRate >= 0,
        icon: BanknotesIcon,
        iconWrap: 'bg-rose-100 text-rose-800 shadow-inner dark:bg-rose-950/40 dark:text-rose-400',
        link: '/benefits',
      },
      {
        title: "Taux d'occupation",
        value: `${data.classrooms?.occupancyRate || 0}%`,
        change: `${data.classrooms?.occupancyGrowth > 0 ? '+' : ''}${data.classrooms?.occupancyGrowth || 0}%`,
        positive: data.classrooms?.occupancyGrowth >= 0,
        icon: BuildingLibraryIcon,
        iconWrap:
          'bg-amber-100 text-amber-800 shadow-inner dark:bg-amber-950/40 dark:text-amber-300',
        link: '/classrooms',
      },
    ];
  }, [dashboardData]);

  const formationDistribution = React.useMemo(() => {
    if (courseDistribution.length === 0) {
      return [{ name: 'Aucune donnée', count: 0, color: 'from-slate-300 to-slate-400' }];
    }

    return courseDistribution
      .sort((a, b) => b.value - a.value)
      .slice(0, 3)
      .map((item, index) => {
        const colors = [
          'from-forma-500 to-forma-700',
          'from-emerald-500 to-emerald-700',
          'from-slate-500 to-slate-700',
        ];

        return {
          name: item.name,
          count: item.value,
          color: colors[index],
        };
      });
  }, [courseDistribution]);

  const upcomingCoursesData = React.useMemo(() => {
    const sessions = sessionsData?.data?.schedules || [];

    return sessions.map((session) => ({
      id: session.id,
      title: session.Course?.name || 'Cours sans nom',
      startDate: session.startTime,
      students: session.currentAttendees || 0,
      room: session.Classroom?.name || 'Salle non définie',
    }));
  }, [sessionsData]);

  const totalCourses = React.useMemo(() => {
    return courseDistribution.reduce((sum, c) => sum + c.value, 0) || 1;
  }, [courseDistribution]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const isLoading =
    dashboardLoading || coursesLoading || sessionsLoading || classroomsLoading || benefitsLoading;

  const tooltipStyles = {
    backgroundColor: chart.tooltipBg,
    borderColor: chart.tooltipBorder,
    borderRadius: 12,
  };

  const formaLinkBtn = FORMA_LINK_BTN;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tableau de bord"
        description="Vue synthétique de l’activité pédagogique, des revenus et de l’occupation."
        actions={
          <Link to="/courses/add" className={formaLinkBtn}>
            <PlusIcon className="h-5 w-5" aria-hidden />
            Nouveau cours
          </Link>
        }
      />

      <UiCard padding={false} className="overflow-hidden">
        <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
          <div className="grid gap-6 p-6 sm:grid-cols-2 sm:gap-5 xl:grid-cols-4">
            {mainStats.map((stat, index) => (
              <motion.div
                key={stat.title}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                whileHover={{ y: -3 }}
                className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
              >
                <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                  />
                  <div className="relative flex gap-4">
                    <div
                      className={clsx(
                        'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                        stat.iconWrap
                      )}
                    >
                      <stat.icon className="h-6 w-6" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {stat.title}
                      </p>
                      <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                        {isLoading ? '…' : stat.value}
                      </p>
                      <div
                        className={clsx(
                          'mt-1 flex items-center gap-1 border-t border-slate-100 pt-2 text-xs font-medium dark:border-slate-700/70',
                          stat.positive
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-slate-500 dark:text-slate-400'
                        )}
                      >
                        <ArrowTrendingUpIcon
                          className={clsx('h-3.5 w-3.5', !stat.positive && 'rotate-180')}
                          aria-hidden
                        />
                        {isLoading ? '…' : stat.change}
                      </div>
                      <Link to={stat.link} className={`mt-2 inline-flex text-xs font-semibold ${linkSubtle}`}>
                        Voir les détails →
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </UiCard>

      <UiCard
        title="Répartition par type de formation"
        actions={
          <Link to="/courses" className={linkSubtle}>
            Tous les cours
          </Link>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {formationDistribution.map((item) => (
            <div
              key={item.name}
              className="rounded-xl border border-slate-100 bg-slate-50/80 p-4 dark:border-slate-700/80 dark:bg-slate-950/40"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.name}</h4>
                <span
                  className={`rounded-full bg-gradient-to-r px-2.5 py-0.5 text-xs font-semibold text-white shadow-sm ${item.color}`}
                >
                  {item.count}
                </span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200/90 dark:bg-slate-800">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${item.color}`}
                  style={{
                    width: `${totalCourses ? ((item.count / totalCourses) * 100).toFixed(1) : 0}%`,
                  }}
                />
              </div>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                {totalCourses ? ((item.count / totalCourses) * 100).toFixed(1) : 0}% des formations
              </p>
            </div>
          ))}
        </div>
      </UiCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <UiCard
          title="Revenus mensuels"
          actions={
            <Link to="/benefits" className={linkSubtle}>
              Bénéfices
            </Link>
          }
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.muted} />
                <XAxis dataKey="name" stroke={chart.axis} tickLine={false} fontSize={11} />
                <YAxis
                  stroke={chart.axis}
                  tickLine={false}
                  tickFormatter={(value) => `${value / 1000}k`}
                  fontSize={11}
                />
                <Tooltip
                  formatter={(value) => [`${formatCurrency(value)}`, 'Montant']}
                  contentStyle={tooltipStyles}
                  itemStyle={{ color: chart.tooltipFg }}
                  labelStyle={{ color: chart.tooltipFg }}
                />
                <Bar dataKey="montant" fill="url(#dashboardRevenueGrad)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="dashboardRevenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chart.barGradientStart} stopOpacity={0.95} />
                    <stop offset="100%" stopColor={chart.barGradientEnd} stopOpacity={0.85} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </UiCard>

        <UiCard
          title="Distribution des formations"
          actions={
            <Link to="/courses" className={linkSubtle}>
              Gérer les cours
            </Link>
          }
        >
          <div className="flex h-72 items-center">
            <div className="flex h-full w-full items-center gap-4">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={courseDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={54}
                    outerRadius={74}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {courseDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} cours`, 'Nombre']}
                    contentStyle={tooltipStyles}
                    itemStyle={{ color: chart.tooltipFg }}
                    labelStyle={{ color: chart.tooltipFg }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {courseDistribution.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className="flex-1 truncate text-slate-700 dark:text-slate-300">{entry.name}</span>
                    <span className="font-semibold tabular-nums text-slate-900 dark:text-white">
                      {totalCourses > 0 ? Math.round((entry.value / totalCourses) * 100) : 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </UiCard>
      </div>

      <UiCard padding={false}>
        <div className="flex flex-col gap-1 border-b border-slate-100 px-6 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Prochains cours</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">Sessions planifiées à venir</p>
          </div>
          <Link to="/sessions" className={linkSubtle}>
            Voir tout
          </Link>
        </div>

        <div className="p-0">
          {isLoading ? (
            <p className="px-6 py-10 text-center text-sm text-slate-600 dark:text-slate-400">
              Chargement des cours…
            </p>
          ) : upcomingCoursesData.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <CalendarDaysIcon className="mb-4 h-14 w-14 text-slate-300 dark:text-slate-600" aria-hidden />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Aucun cours à venir</h3>
              <p className="mt-2 max-w-md text-sm text-slate-600 dark:text-slate-400">
                Planifiez une session depuis les cours ou l’emploi du temps pour la voir ici.
              </p>
              <Link to="/courses/add" className={`mt-6 ${formaLinkBtn}`}>
                <PlusIcon className="h-5 w-5" />
                Planifier un cours
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead className="bg-slate-50/90 dark:bg-slate-950/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Cours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Date de début
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Étudiants
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Salle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {upcomingCoursesData.map((course) => (
                    <motion.tr
                      key={course.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="transition-colors hover:bg-slate-50/90 dark:hover:bg-slate-800/40"
                    >
                      <td className="whitespace-nowrap px-6 py-3.5 text-sm font-medium text-slate-900 dark:text-white">
                        {course.title}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3.5 text-sm text-slate-600 dark:text-slate-400">
                        {new Date(course.startDate).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3.5">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
                          <AcademicCapIcon className="h-4 w-4 text-forma-600 dark:text-forma-400" aria-hidden />
                          {course.students}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-3.5 text-sm text-slate-600 dark:text-slate-400">
                        {course.room}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3.5">
                        <Link
                          to={`/sessions/${course.id}`}
                          className="inline-flex rounded-lg border border-slate-200 bg-white p-2 text-forma-700 shadow-sm transition hover:border-forma-200 hover:bg-forma-50 dark:border-slate-700 dark:bg-slate-900 dark:text-forma-300 dark:hover:bg-slate-800"
                          title="Voir les détails"
                          aria-label="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </UiCard>

      <UiCard
        title="Taux d'occupation hebdomadaire des salles"
        actions={
          <Link to="/classrooms" className={linkSubtle}>
            Gérer les salles
          </Link>
        }
      >
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={occupancyData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.muted} />
              <XAxis dataKey="name" stroke={chart.axis} tickLine={false} fontSize={11} />
              <YAxis
                stroke={chart.axis}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                fontSize={11}
              />
              <Tooltip
                formatter={(value) => [`${value}%`, "Taux d'occupation"]}
                contentStyle={tooltipStyles}
                itemStyle={{ color: chart.tooltipFg }}
                labelStyle={{ color: chart.tooltipFg }}
              />
              <Line
                type="monotone"
                dataKey="occupancy"
                stroke={chart.line}
                strokeWidth={2.5}
                dot={{ r: 4, stroke: chart.line, fill: chart.line }}
                activeDot={{ r: 6, stroke: chart.line, fill: chart.line }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </UiCard>
    </div>
  );
};

export default Dashboard;
